import React, { useState, useEffect, useCallback, useMemo } from 'react';
import client from '../api/client';
import { useSchedulePermissions, PAGE_PERMISSION_IDS } from '../hooks/usePermissions';
import { useToast } from '../context/ToastContext';
import ModernGanttChart from '../components/Dashboard/ModernGanttChart';
import ScheduleToolbar from '../components/ScheduleToolbar';
import GlassButton from '../components/GlassButton';
import CitySearchSelect from '../components/CitySearchSelect';
import QuickDatePicker from '../components/QuickDatePicker';
import RowActions from '../components/RowActions';
import { AddRowButtonCompact } from '../components/AddRowButton';
import { saveScheduleOrder, applyScheduleOrder } from '../utils/scheduleOrderStorage';
import { useDeleteAllSchedules } from '../hooks/useDeleteAllSchedules';
import { getCollapsedGroups, saveCollapsedGroups, getHiddenRows, saveHiddenRows } from '../utils/hiddenRowsStorage';
import {
  buildOutlineGroups,
  buildChapterColorMap,
  isRowHiddenByOutline,
  getHierarchyRowStyle,
  getHierarchyCellFontWeight,
  getOutlineIndentBorderColor,
  isSectionRow,
  normalizeOutlineNumber,
} from '../utils/scheduleOutlineStyles';
import { saveSelectedCity, getSelectedCity, saveViewMode, getViewMode, getPageFilters, savePageFilters } from '../utils/userPreferences';
import ScheduleSectionPanel from '../components/ScheduleSectionPanel';
import SyncedHorizontalScroll from '../components/SyncedHorizontalScroll';
import { validateDates, prepareRowForCopy, formatDateDisplay, adjustAutoResizeTextareas } from '../utils/scheduleHelpers';
import StageAutocomplete, { StageColoredReadonly } from '../components/StageAutocomplete';
import { createScheduleUpdateHandler } from '../utils/scheduleUpdateWithCascade';
import PRE_CONSTRUCTION_TEMPLATE from '../utils/preConstructionTemplate';
import TZ_CONSTRUCTION_TEMPLATE from '../utils/tzConstructionTemplate';
import { postSchedulesBatch, persistOrderAfterBatchFill } from '../utils/scheduleBatchUtils';
import {
  mergeTzConstructionRow,
  isTzFieldEditable,
} from '../utils/tzConstructionSync';

const SYNCED_ROW_GUARD_MSG =
  'Строка связана с графиком строительства — удаление и скрытие недоступны';

const SCHEDULE_TYPE = 'preconstruction';
const CONSTRUCTION_SCHEDULE_TYPE = 'construction';
const TZ_CONSTRUCTION_SCHEDULE_TYPE = 'tz_construction';
const PANEL_PREFS_KEY = 'preconstruction_panels';

const TZ_CALENDAR_MODES = [
  { id: 'all', icon: '📅', label: 'Общий календарь' },
  { id: 'preconstruction', icon: '📋', label: 'До начала строительства' },
  { id: 'construction', icon: '🏗️', label: 'Строительство' },
];

const CALENDAR_MODE_TITLES = {
  all: 'Общий календарь — до начала строительства и строительство',
  preconstruction: 'Календарь — график до начала строительства',
  construction: 'Календарь — график строительства',
};

const getPanelPrefs = () => getPageFilters(PANEL_PREFS_KEY);
const savePanelPref = (patch) => savePageFilters(PANEL_PREFS_KEY, { ...getPanelPrefs(), ...patch });

const loadCalendarMode = () => {
  const fromPanels = getPanelPrefs().calendarMode;
  if (fromPanels && TZ_CALENDAR_MODES.some((m) => m.id === fromPanels)) return fromPanels;
  return getViewMode(SCHEDULE_TYPE) === 'calendar' ? 'all' : null;
};

const EXPORT_COLUMNS = [
  { field: 'construction_stage', label: '№ п/п', type: 'text' },
  { field: 'sections', label: 'Этап строительства', type: 'text' },
  { field: 'work_name', label: 'Наименование работ', type: 'text' },
  { field: 'planned_start_date', label: 'План начало', type: 'date' },
  { field: 'planned_end_date', label: 'План конец', type: 'date' },
  { field: 'actual_start_date', label: 'Факт начало', type: 'date' },
  { field: 'actual_end_date', label: 'Факт конец', type: 'date' },
  { field: 'service', label: 'Исполнитель', type: 'text' },
  { field: 'responsible_employee', label: 'Ответственный', type: 'text' },
  { field: 'vacancy', label: 'Документы', type: 'text' },
];

// Логика как в Excel: =ЕСЛИ((REF-start)/(end-start)<0;0;ЕСЛИ((REF-start)/(end-start)>100%;100%;...))
// REF = текущая дата, start = начало периода, end = конец периода
const calcReadiness = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const ref = new Date(); // REF = сегодня
  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end <= start) return null;
  const numerator = ref - start;
  const denominator = end - start;
  let pct = (numerator / denominator) * 100;
  if (pct < 0) pct = 0;
  if (pct > 100) pct = 100;
  return Math.round(pct);
};

const calcDelay = (plannedEnd, actualEnd) => {
  if (!plannedEnd || !actualEnd) return null;
  const pe = new Date(plannedEnd);
  const ae = new Date(actualEnd);
  if (isNaN(pe) || isNaN(ae)) return null;
  return Math.round((ae - pe) / (1000 * 60 * 60 * 24));
};

const PreConstructionSchedule = () => {
  const [schedules, setSchedules] = useState([]);
  const [tzConstructionSchedules, setTzConstructionSchedules] = useState([]);
  const [constructionSourceSchedules, setConstructionSourceSchedules] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState(null);
  const [calendarMode, setCalendarMode] = useState(loadCalendarMode);
  const [unsavedRows, setUnsavedRows] = useState({});
  const [searchText, setSearchText] = useState('');
  const [dateErrors, setDateErrors] = useState({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [constructionSearchText, setConstructionSearchText] = useState('');
  const [tzConstructionTemplateLoading, setTzConstructionTemplateLoading] = useState(false);
  const [stages, setStages] = useState([]);
  const [collapsedGroupParentIds, setCollapsedGroupParentIds] = useState(() => new Set());
  const [collapsedTzGroupParentIds, setCollapsedTzGroupParentIds] = useState(() => new Set());
  const [hiddenPreconstructionIds, setHiddenPreconstructionIds] = useState(() => new Set());
  const [hiddenTzConstructionIds, setHiddenTzConstructionIds] = useState(() => new Set());
  const [mainTableExpanded, setMainTableExpanded] = useState(
    () => getPanelPrefs().mainTableExpanded !== false
  );
  const [secondaryTableExpanded, setSecondaryTableExpanded] = useState(
    () => getPanelPrefs().secondaryTableExpanded !== false
  );
  const { showSuccess, showError, showInfo, showWarning } = useToast();
  const { requestDeleteAll, deleteAllModal } = useDeleteAllSchedules();
  const { canEditSchedule: canEdit } = useSchedulePermissions(PAGE_PERMISSION_IDS.preconstruction);

  useEffect(() => {
    if (selectedCity) {
      setCollapsedGroupParentIds(getCollapsedGroups(selectedCity, SCHEDULE_TYPE));
      setCollapsedTzGroupParentIds(getCollapsedGroups(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE));
      setHiddenPreconstructionIds(getHiddenRows(selectedCity, SCHEDULE_TYPE));
      setHiddenTzConstructionIds(getHiddenRows(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE));
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedCity) {
      saveCollapsedGroups(selectedCity, SCHEDULE_TYPE, collapsedGroupParentIds);
    }
  }, [selectedCity, collapsedGroupParentIds]);

  useEffect(() => {
    if (selectedCity) {
      saveCollapsedGroups(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE, collapsedTzGroupParentIds);
    }
  }, [selectedCity, collapsedTzGroupParentIds]);

  useEffect(() => {
    if (selectedCity) {
      saveHiddenRows(selectedCity, SCHEDULE_TYPE, hiddenPreconstructionIds);
    }
  }, [selectedCity, hiddenPreconstructionIds]);

  useEffect(() => {
    if (selectedCity) {
      saveHiddenRows(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE, hiddenTzConstructionIds);
    }
  }, [selectedCity, hiddenTzConstructionIds]);

  useEffect(() => {
    const t1 = setTimeout(adjustAutoResizeTextareas, 0);
    const t2 = setTimeout(adjustAutoResizeTextareas, 120);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [
    schedules,
    tzConstructionSchedules,
    constructionSearchText,
    mainTableExpanded,
    secondaryTableExpanded,
    searchText,
    collapsedGroupParentIds,
  ]);

  useEffect(() => {
    const errors = {};
    [...schedules, ...tzConstructionSchedules].forEach(s => {
      const planValidation = validateDates(s.planned_start_date, s.planned_end_date);
      const factValidation = validateDates(s.actual_start_date, s.actual_end_date);
      if (!planValidation.valid) errors[`${s.id}_plan`] = planValidation.error;
      if (!factValidation.valid) errors[`${s.id}_fact`] = factValidation.error;
    });
    setDateErrors(errors);
  }, [schedules, tzConstructionSchedules]);

  const fetchCities = async () => {
    try {
      const response = await client.get('/cities');
      const visibleCities = response.data.filter(city => city.visible_in_schedules !== false);
      setCities(visibleCities);
      if (visibleCities.length > 0) {
        const savedCity = getSelectedCity();
        const cityExists = visibleCities.some(c => c.id === savedCity);
        setSelectedCity(cityExists ? savedCity : visibleCities[0].id);
      }
    } catch (error) {
      showError('Ошибка при загрузке списка объектов');
    }
  };

  const fetchStages = async () => {
    try {
      const response = await client.get('/construction-stages', { params: { active_only: true } });
      setStages(response.data);
    } catch (error) {
      console.error('Error fetching stages:', error);
    }
  };

  const fetchSchedules = async () => {
    try {
      const response = await client.get('/schedules', {
        params: { schedule_type: SCHEDULE_TYPE, city_id: selectedCity }
      });
      const savedSchedules = response.data;
      const cityUnsavedRows = unsavedRows[selectedCity] || [];
      let allSchedules = [...savedSchedules, ...cityUnsavedRows];
      allSchedules = applyScheduleOrder(allSchedules, selectedCity, SCHEDULE_TYPE);
      setSchedules(allSchedules);
    } catch (error) {
      showError('Ошибка при загрузке графика');
    }
  };

  const fetchTzConstructionSchedules = async () => {
    try {
      const response = await client.get('/schedules', {
        params: { schedule_type: TZ_CONSTRUCTION_SCHEDULE_TYPE, city_id: selectedCity },
      });
      let rows = response.data || [];
      rows = applyScheduleOrder(rows, selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE);
      setTzConstructionSchedules(rows);
    } catch (error) {
      showError('Ошибка при загрузке графика строительства (ТЗ)');
    }
  };

  const fetchConstructionSourceSchedules = async () => {
    try {
      const response = await client.get('/schedules', {
        params: { schedule_type: CONSTRUCTION_SCHEDULE_TYPE, city_id: selectedCity },
      });
      let rows = response.data || [];
      rows = applyScheduleOrder(rows, selectedCity, CONSTRUCTION_SCHEDULE_TYPE);
      setConstructionSourceSchedules(rows);
    } catch (error) {
      showError('Ошибка при загрузке данных стройки для синхронизации');
    }
  };

  const refreshScheduleData = async () => {
    await fetchSchedules();
    await fetchTzConstructionSchedules();
    await fetchConstructionSourceSchedules();
  };

  useEffect(() => {
    fetchCities();
    fetchStages();
  }, []);

  useEffect(() => {
    if (selectedCity) {
      fetchSchedules();
      fetchTzConstructionSchedules();
      fetchConstructionSourceSchedules();
    }
  }, [selectedCity]);

  useEffect(() => {
    if (selectedCity && secondaryTableExpanded) {
      fetchConstructionSourceSchedules();
    }
  }, [secondaryTableExpanded, selectedCity]);

  const handleScheduleUpdate = createScheduleUpdateHandler({
    fetchSchedules: refreshScheduleData, showError, cityId: selectedCity
  });

  const handleCityChange = (cityId) => {
    setSelectedCity(cityId);
    saveSelectedCity(cityId);
  };

  const handleCalendarModeChange = (mode) => {
    setCalendarMode(mode);
    savePanelPref({ calendarMode: mode });
    saveViewMode(SCHEDULE_TYPE, mode ? 'calendar' : 'table');
  };

  const tzConstructionDisplayRows = useMemo(
    () => tzConstructionSchedules.map((row) => mergeTzConstructionRow(row, constructionSourceSchedules)),
    [tzConstructionSchedules, constructionSourceSchedules]
  );

  const calendarSchedules = useMemo(() => {
    const pre = schedules.map((s) => ({ ...s, schedule_type: SCHEDULE_TYPE }));
    const tzCon = tzConstructionDisplayRows.map((s) => ({
      ...s,
      schedule_type: TZ_CONSTRUCTION_SCHEDULE_TYPE,
    }));
    if (calendarMode === 'preconstruction') return pre;
    if (calendarMode === 'construction') return tzCon;
    if (calendarMode === 'all') return [...pre, ...tzCon];
    return [];
  }, [schedules, tzConstructionDisplayRows, calendarMode]);

  const calendarAllowedTypes = useMemo(() => {
    if (calendarMode === 'preconstruction') return [SCHEDULE_TYPE];
    if (calendarMode === 'construction') return [TZ_CONSTRUCTION_SCHEDULE_TYPE];
    if (calendarMode === 'all') return [SCHEDULE_TYPE, TZ_CONSTRUCTION_SCHEDULE_TYPE];
    return null;
  }, [calendarMode]);

  const getFilteredSchedules = useCallback(() => {
    let filtered = schedules;
    if (searchText) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(s => s.isNew
        || s.work_name?.toLowerCase().includes(search)
        || s.sections?.toLowerCase().includes(search)
        || s.construction_stage?.toLowerCase().includes(search));
    }
    return filtered;
  }, [schedules, searchText]);

  const toggleMainTable = () => {
    setMainTableExpanded((v) => {
      const next = !v;
      savePanelPref({ mainTableExpanded: next });
      return next;
    });
  };

  const toggleSecondaryTable = () => {
    setSecondaryTableExpanded((v) => {
      const next = !v;
      savePanelPref({ secondaryTableExpanded: next });
      return next;
    });
  };

  const toggleGroupCollapse = (parentId) => {
    setCollapsedGroupParentIds(prev => {
      const next = new Set(prev);
      const sid = String(parentId);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const toggleTzGroupCollapse = (parentId) => {
    setCollapsedTzGroupParentIds((prev) => {
      const next = new Set(prev);
      const sid = String(parentId);
      if (next.has(sid)) next.delete(sid);
      else next.add(sid);
      return next;
    });
  };

  const hideRow = (id, scheduleType) => {
    if (!canEdit) return;
    const sid = String(id);
    if (scheduleType === SCHEDULE_TYPE) {
      setHiddenPreconstructionIds((prev) => new Set([...prev, sid]));
    } else {
      setHiddenTzConstructionIds((prev) => new Set([...prev, sid]));
    }
    showInfo('Строка скрыта');
  };

  const unhideAllRows = (scheduleType) => {
    if (scheduleType === SCHEDULE_TYPE) {
      setHiddenPreconstructionIds(new Set());
    } else {
      setHiddenTzConstructionIds(new Set());
    }
    showInfo('Все скрытые строки показаны');
  };

  const guardSyncedRow = (row, action) => {
    if (row?.isSyncedFromConstruction) {
      showInfo(SYNCED_ROW_GUARD_MSG);
      return true;
    }
    if (typeof action === 'function') action();
    return false;
  };

  const expandAllGroups = () => {
    setCollapsedGroupParentIds(new Set());
    if (selectedCity) saveCollapsedGroups(selectedCity, SCHEDULE_TYPE, new Set());
    showInfo('Все группы развёрнуты');
  };

  const collapseAllGroups = () => {
    const base = getFilteredSchedules().filter((s) => !hiddenPreconstructionIds.has(String(s.id)));
    const { groups } = buildOutlineGroups(base);
    const parentIds = new Set([...groups.keys()].map((i) => String(base[i].id)));
    setCollapsedGroupParentIds(parentIds);
    showInfo('Все группы свёрнуты');
  };

  const expandAllTzGroups = () => {
    setCollapsedTzGroupParentIds(new Set());
    if (selectedCity) saveCollapsedGroups(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE, new Set());
    showInfo('Все группы развёрнуты');
  };

  const collapseAllTzGroups = () => {
    const base = getFilteredTzConstructionDisplay().filter((s) => !hiddenTzConstructionIds.has(String(s.id)));
    const { groups } = buildOutlineGroups(base);
    const parentIds = new Set([...groups.keys()].map((i) => String(base[i].id)));
    setCollapsedTzGroupParentIds(parentIds);
    showInfo('Все группы свёрнуты');
  };

  const saveCell = async (scheduleId, field, value) => {
    if (!canEdit) return;
    try {
      const isNewRow = scheduleId.toString().startsWith('new-');
      let valueToSave = value;
      if (field === 'construction_stage') {
        valueToSave = normalizeOutlineNumber(value);
      }
      
      if (isNewRow) {
        const updatedSchedules = schedules.map(s =>
          s.id === scheduleId ? { ...s, [field]: valueToSave } : s
        );
        setSchedules(updatedSchedules);
        
        const cityUnsaved = updatedSchedules.filter(s => 
          s.id.toString().startsWith('new-') && s.city_id === selectedCity
        );
        setUnsavedRows(prev => ({ ...prev, [selectedCity]: cityUnsaved }));
        
        const schedule = updatedSchedules.find(s => s.id === scheduleId);
        const canSave = schedule.work_name && schedule.planned_start_date && schedule.planned_end_date;
        
        if (canSave) {
          const dateValidation = validateDates(schedule.planned_start_date, schedule.planned_end_date);
          if (!dateValidation.valid) {
            showWarning(dateValidation.error);
            return;
          }

          const requestData = {
            schedule_type: SCHEDULE_TYPE,
            city_id: parseInt(selectedCity),
            planned_start_date: schedule.planned_start_date,
            planned_end_date: schedule.planned_end_date,
            work_name: schedule.work_name.trim(),
            ...(schedule.construction_stage && { construction_stage: normalizeOutlineNumber(schedule.construction_stage) || schedule.construction_stage.trim() }),
            ...(schedule.sections && { sections: schedule.sections.trim() }),
            ...(schedule.service && { service: schedule.service.trim() }),
            ...(schedule.responsible_employee && { responsible_employee: schedule.responsible_employee.trim() }),
            ...(schedule.vacancy && { vacancy: schedule.vacancy.trim() }),
            ...(schedule.actual_start_date && { actual_start_date: schedule.actual_start_date }),
            ...(schedule.actual_end_date && { actual_end_date: schedule.actual_end_date })
          };
          
          const response = await client.post('/schedules', requestData);
          setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...response.data, isNew: false } : s));
          setUnsavedRows(prev => {
            const updated = { ...prev };
            updated[selectedCity] = updated[selectedCity]?.filter(row => row.id !== scheduleId) || [];
            return updated;
          });
          showSuccess('Запись успешно создана');
        }
      } else {
        let processedValue = valueToSave;
        if (typeof processedValue === 'string') {
          processedValue = processedValue.trim() || null;
        }
        if (field === 'construction_stage') {
          processedValue = normalizeOutlineNumber(processedValue);
        }
        await client.put(`/schedules/${scheduleId}`, { [field]: processedValue });
        setSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, [field]: processedValue } : s));
      }
    } catch (error) {
      showError('Ошибка при сохранении данных');
      fetchSchedules();
    }
  };

  const saveTzConstructionCell = async (scheduleId, field, value, isSynced) => {
    if (!canEdit) return;
    if (!isTzFieldEditable(field, isSynced)) {
      showInfo('Даты и наименование для работ стройки подтягиваются автоматически и не редактируются здесь');
      return;
    }
    try {
      const isNewRow = scheduleId.toString().startsWith('new-');
      let valueToSave = value;
      if (field === 'construction_stage') {
        valueToSave = normalizeOutlineNumber(value);
      }
      if (isNewRow) {
        const updated = tzConstructionSchedules.map(s =>
          s.id === scheduleId ? { ...s, [field]: valueToSave } : s
        );
        setTzConstructionSchedules(updated);
        const schedule = updated.find(s => s.id === scheduleId);
        const canSave = schedule?.work_name;
        if (canSave && (field === 'planned_start_date' || field === 'planned_end_date')) {
          if (!schedule.planned_start_date || !schedule.planned_end_date) return;
          const dateValidation = validateDates(schedule.planned_start_date, schedule.planned_end_date);
          if (!dateValidation.valid) {
            showWarning(dateValidation.error);
            return;
          }
          const requestData = {
            schedule_type: TZ_CONSTRUCTION_SCHEDULE_TYPE,
            city_id: parseInt(selectedCity, 10),
            work_name: schedule.work_name.trim(),
            ...(schedule.sections && { sections: schedule.sections.trim() }),
            ...(schedule.construction_stage && {
              construction_stage: normalizeOutlineNumber(schedule.construction_stage) || schedule.construction_stage.trim(),
            }),
            planned_start_date: schedule.planned_start_date,
            planned_end_date: schedule.planned_end_date,
            ...(schedule.service && { service: schedule.service.trim() }),
            ...(schedule.responsible_employee && { responsible_employee: schedule.responsible_employee.trim() }),
            ...(schedule.vacancy && { vacancy: schedule.vacancy.trim() }),
          };
          const response = await client.post('/schedules', requestData);
          setTzConstructionSchedules(prev => prev.map(s => s.id === scheduleId ? { ...response.data, isNew: false } : s));
          showSuccess('Запись успешно создана');
        }
      } else {
        let processedValue = valueToSave;
        if (typeof processedValue === 'string') {
          processedValue = processedValue.trim() || null;
        }
        if (field === 'construction_stage') {
          processedValue = normalizeOutlineNumber(processedValue);
        }
        await client.put(`/schedules/${scheduleId}`, { [field]: processedValue });
        setTzConstructionSchedules(prev => prev.map(s => s.id === scheduleId ? { ...s, [field]: processedValue } : s));
      }
    } catch (error) {
      showError('Ошибка при сохранении данных');
      fetchTzConstructionSchedules();
    }
  };

  const createEmptyRow = () => ({
    id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    schedule_type: SCHEDULE_TYPE,
    city_id: selectedCity,
    construction_stage: '',
    sections: '',
    work_name: '',
    service: '',
    responsible_employee: '',
    vacancy: '',
    planned_start_date: '',
    planned_end_date: '',
    actual_start_date: null,
    actual_end_date: null,
    isNew: true
  });

  const addNewRow = () => {
    if (!canEdit) return;
    if (!selectedCity) { showInfo('Пожалуйста, выберите город'); return; }
    setSchedules(prev => [...prev, createEmptyRow()]);
    showInfo('Добавлена новая строка');
  };

  const insertRowAt = (index, position) => {
    if (!canEdit) return;
    if (!selectedCity) { showInfo('Пожалуйста, выберите город'); return; }
    const base = getFilteredSchedules().filter((s) => !hiddenPreconstructionIds.has(String(s.id)));
    const { groups } = buildOutlineGroups(base);
    const list = base.filter((_, idx) => !isRowHiddenByOutline(idx, groups, collapsedGroupParentIds, base));
    const targetSchedule = list[index];
    const realIndex = schedules.findIndex(s => s.id === targetSchedule.id);
    const insertIndex = position === 'above' ? realIndex : realIndex + 1;
    const newSchedules = [...schedules];
    newSchedules.splice(insertIndex, 0, createEmptyRow());
    setSchedules(newSchedules);
    saveScheduleOrder(selectedCity, SCHEDULE_TYPE, newSchedules);
  };

  const createEmptyTzRow = () => ({
    id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
    schedule_type: TZ_CONSTRUCTION_SCHEDULE_TYPE,
    city_id: selectedCity,
    construction_stage: '',
    sections: '',
    work_name: '',
    service: '',
    responsible_employee: '',
    vacancy: '',
    planned_start_date: '',
    planned_end_date: '',
    actual_start_date: null,
    actual_end_date: null,
    isNew: true,
  });

  const insertTzConstructionRowAt = (index, position, displayList) => {
    if (!canEdit) return;
    if (!selectedCity) { showInfo('Пожалуйста, выберите объект'); return; }
    const targetSchedule = displayList[index];
    if (guardSyncedRow(targetSchedule)) return;
    const realIndex = tzConstructionSchedules.findIndex((s) => s.id === targetSchedule.id);
    const insertIndex = position === 'above' ? realIndex : realIndex + 1;
    const newSchedules = [...tzConstructionSchedules];
    newSchedules.splice(insertIndex, 0, createEmptyTzRow());
    setTzConstructionSchedules(newSchedules);
    saveScheduleOrder(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE, newSchedules);
  };

  const getFilteredTzConstructionDisplay = useCallback(() => {
    let filtered = tzConstructionDisplayRows;
    if (constructionSearchText) {
      const search = constructionSearchText.toLowerCase();
      filtered = filtered.filter(s => s.isNew
        || s.work_name?.toLowerCase().includes(search)
        || s.sections?.toLowerCase().includes(search)
        || s.construction_stage?.toLowerCase().includes(search));
    }
    return filtered;
  }, [tzConstructionDisplayRows, constructionSearchText]);

  const fillFromTzConstructionTemplate = async () => {
    if (!selectedCity) {
      showInfo('Пожалуйста, выберите объект');
      return;
    }
    if (tzConstructionSchedules.length > 0) {
      if (!window.confirm(
        'В графике строительства (ТЗ) уже есть данные. Заполнение добавит строки в конец. Продолжить?'
      )) return;
    }

    setTzConstructionTemplateLoading(true);
    try {
      const batchData = TZ_CONSTRUCTION_TEMPLATE.map((item) => ({
        schedule_type: TZ_CONSTRUCTION_SCHEDULE_TYPE,
        city_id: parseInt(selectedCity, 10),
        construction_stage: normalizeOutlineNumber(item.number) || item.number || '',
        sections: item.stage || '',
        work_name: item.name,
        planned_start_date: null,
        planned_end_date: null,
      }));

      const existingSaved = tzConstructionSchedules.filter(
        (s) => s?.id != null && !String(s.id).startsWith('new-')
      );
      const created = await postSchedulesBatch(client, batchData);
      persistOrderAfterBatchFill(
        selectedCity,
        TZ_CONSTRUCTION_SCHEDULE_TYPE,
        created,
        existingSaved,
        existingSaved.length === 0
      );

      showSuccess(`График строительства (ТЗ): добавлено ${batchData.length} строк`);
      await fetchTzConstructionSchedules();
      await fetchConstructionSourceSchedules();
    } catch (error) {
      console.error('TZ construction template error:', error);
      showError('Ошибка при заполнении шаблона');
    } finally {
      setTzConstructionTemplateLoading(false);
    }
  };

  const deleteAllTzConstructionForCity = () => {
    const cityName = cities.find((c) => c.id === selectedCity)?.name || 'выбранного объекта';
    const total = tzConstructionSchedules.length;
    requestDeleteAll({
      total,
      cityName,
      scheduleType: TZ_CONSTRUCTION_SCHEDULE_TYPE,
      cityId: selectedCity,
      scheduleLabel: 'графика строительства (ТЗ)',
      orderStorageKey: TZ_CONSTRUCTION_SCHEDULE_TYPE,
      onSuccess: () => setTzConstructionSchedules([]),
    });
  };

  const fillFromTemplate = async () => {
    if (!selectedCity) { showInfo('Пожалуйста, выберите город'); return; }
    const existingSaved = schedules.filter(
      (s) => s?.id != null && !String(s.id).startsWith('new-')
    );
    if (existingSaved.length > 0) {
      if (!window.confirm('В графике до начала строительства уже есть данные. Заполнение по шаблону добавит строки в конец. Продолжить?')) return;
    }

    setTemplateLoading(true);
    try {
      const batchData = PRE_CONSTRUCTION_TEMPLATE.map(item => ({
        schedule_type: SCHEDULE_TYPE,
        city_id: parseInt(selectedCity),
        construction_stage: normalizeOutlineNumber(item.number) || item.number || '',
        sections: item.stage || '',
        work_name: item.name,
        planned_start_date: null,
        planned_end_date: null,
      }));

      const created = await postSchedulesBatch(client, batchData);
      persistOrderAfterBatchFill(
        selectedCity,
        SCHEDULE_TYPE,
        created,
        existingSaved,
        existingSaved.length === 0
      );

      showSuccess(`График до начала строительства: добавлено ${batchData.length} строк`);
      await fetchSchedules();
    } catch (error) {
      console.error('Template fill error:', error);
      showError('Ошибка при заполнении шаблона');
    } finally {
      setTemplateLoading(false);
    }
  };

  const deleteAllForCity = () => {
    const cityName = cities.find((c) => c.id === selectedCity)?.name || 'выбранного объекта';
    const isPreconstructionRow = (s) => s.schedule_type === SCHEDULE_TYPE || s.schedule_type == null;
    const total = schedules.filter(isPreconstructionRow).length;
    requestDeleteAll({
      total,
      cityName,
      scheduleType: SCHEDULE_TYPE,
      cityId: selectedCity,
      scheduleLabel: 'графика до начала строительства',
      orderStorageKey: SCHEDULE_TYPE,
      onSuccess: () => {
        setSchedules([]);
        setUnsavedRows((prev) => ({ ...prev, [selectedCity]: [] }));
      },
    });
  };

  const copyRow = (schedule) => {
    if (!canEdit) return;
    const copy = prepareRowForCopy(schedule, SCHEDULE_TYPE);
    copy.city_id = selectedCity;
    setSchedules(prev => [...prev, copy]);
    showSuccess('Строка скопирована');
  };

  const deleteRow = async (id) => {
    if (!canEdit) return;
    if (window.confirm('Удалить эту строку?')) {
      try {
        if (id.toString().startsWith('new-')) {
          setSchedules(prev => prev.filter(s => s.id !== id));
          setUnsavedRows(prev => {
            const updated = { ...prev };
            updated[selectedCity] = updated[selectedCity]?.filter(row => row.id !== id) || [];
            return updated;
          });
        } else {
          await client.delete(`/schedules/${id}`);
          setSchedules(prev => prev.filter(s => s.id !== id));
        }
        setHiddenPreconstructionIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
        showSuccess('Запись удалена');
      } catch (error) {
        showError('Ошибка при удалении');
      }
    }
  };

  const deleteTzConstructionRow = async (id) => {
    if (!canEdit) return;
    if (window.confirm('Удалить эту строку?')) {
      try {
        if (id.toString().startsWith('new-')) {
          setTzConstructionSchedules((prev) => prev.filter((s) => s.id !== id));
        } else {
          await client.delete(`/schedules/${id}`);
          setTzConstructionSchedules((prev) => prev.filter((s) => s.id !== id));
        }
        setHiddenTzConstructionIds((prev) => {
          const next = new Set(prev);
          next.delete(String(id));
          return next;
        });
        showSuccess('Запись удалена');
      } catch (error) {
        showError('Ошибка при удалении');
      }
    }
  };

  const copyTzConstructionRow = (schedule) => {
    if (!canEdit) return;
    if (guardSyncedRow(schedule)) return;
    const copy = prepareRowForCopy(schedule, TZ_CONSTRUCTION_SCHEDULE_TYPE);
    copy.city_id = selectedCity;
    setTzConstructionSchedules((prev) => [...prev, copy]);
    showSuccess('Строка скопирована');
  };

  const moveRow = (fromIndex, toIndex) => {
    if (!canEdit) return;
    const base = getFilteredSchedules().filter((s) => !hiddenPreconstructionIds.has(String(s.id)));
    const { groups } = buildOutlineGroups(base);
    const list = base.filter((_, idx) => !isRowHiddenByOutline(idx, groups, collapsedGroupParentIds, base));
    if (toIndex < 0 || toIndex >= list.length) return;
    const fromSchedule = list[fromIndex];
    const toSchedule = list[toIndex];
    const realFromIndex = schedules.findIndex(s => s.id === fromSchedule.id);
    const realToIndex = schedules.findIndex(s => s.id === toSchedule.id);
    const newSchedules = [...schedules];
    const [movedItem] = newSchedules.splice(realFromIndex, 1);
    newSchedules.splice(realToIndex, 0, movedItem);
    setSchedules(newSchedules);
    saveScheduleOrder(selectedCity, SCHEDULE_TYPE, newSchedules);
  };

  const moveTzConstructionRow = (fromIndex, toIndex, displayList) => {
    if (!canEdit) return;
    if (toIndex < 0 || toIndex >= displayList.length) return;
    const fromSchedule = displayList[fromIndex];
    const toSchedule = displayList[toIndex];
    if (guardSyncedRow(fromSchedule) || guardSyncedRow(toSchedule)) return;
    const realFromIndex = tzConstructionSchedules.findIndex((s) => s.id === fromSchedule.id);
    const realToIndex = tzConstructionSchedules.findIndex((s) => s.id === toSchedule.id);
    const newSchedules = [...tzConstructionSchedules];
    const [movedItem] = newSchedules.splice(realFromIndex, 1);
    newSchedules.splice(realToIndex, 0, movedItem);
    setTzConstructionSchedules(newSchedules);
    saveScheduleOrder(selectedCity, TZ_CONSTRUCTION_SCHEDULE_TYPE, newSchedules);
  };

  const cellBorder = '1px solid var(--border-color)';
  const cellPad = '6px 8px';
  const cellTd = { border: cellBorder, padding: cellPad, verticalAlign: 'top' };
  const autoResizeTaStyle = {
    width: '100%',
    resize: 'none',
    overflow: 'hidden',
    lineHeight: '1.4',
    minHeight: '28px',
  };

  const renderCell = (schedule, field, value) => {
    const isDateField = field.includes('date');
    const hasDateError = dateErrors[`${schedule.id}_${field.includes('actual') ? 'fact' : 'plan'}`];

    if (field === 'construction_stage') {
      return (
        <input
          type="text"
          value={value || ''}
          placeholder="№"
          onChange={(e) => setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, [field]: e.target.value } : s))}
          onBlur={() => saveCell(schedule.id, field, value)}
          onKeyDown={(e) => { if (e.key === 'Enter') saveCell(schedule.id, field, value); }}
          style={{ width: '100%', textAlign: 'center', fontWeight: 500 }}
          disabled={!canEdit}
        />
      );
    }

    if (field === 'sections') {
      if (!canEdit) {
        return <StageColoredReadonly text={value} stages={stages} title="Этап строительства" />;
      }
      return (
        <StageAutocomplete
          value={value || ''}
          stages={stages}
          disabled={!canEdit}
          onChange={(newValue) => {
            setSchedules((prev) =>
              prev.map((s) => (s.id === schedule.id ? { ...s, sections: newValue } : s))
            );
            saveCell(schedule.id, 'sections', newValue);
          }}
          onBlur={() => saveCell(schedule.id, field, value)}
        />
      );
    }

    if (field === 'work_name') {
      return (
        <textarea
          value={value || ''}
          placeholder="Наименование работ"
          onChange={(e) => {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
            setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, [field]: e.target.value } : s));
          }}
          onBlur={() => saveCell(schedule.id, field, value)}
          style={autoResizeTaStyle}
          rows={1}
          disabled={!canEdit}
          data-auto-resize="true"
        />
      );
    }

    if (isDateField) {
      const isEndDate = field.includes('end');
      const relatedDate = field === 'planned_end_date' ? schedule.planned_start_date
        : field === 'actual_start_date' ? schedule.planned_start_date
        : field === 'actual_end_date' ? schedule.planned_end_date
        : null;
      const relativeLabel = field === 'actual_end_date' ? 'От даты окончания' : 'От даты начала';
      return (
        <div style={{ position: 'relative' }}>
          <QuickDatePicker
            value={value}
            onChange={(newValue) => setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, [field]: newValue } : s))}
            onSave={(newValue) => saveCell(schedule.id, field, newValue)}
            disabled={!canEdit}
            isEndDate={isEndDate}
            relatedDate={relatedDate}
            relativeLabel={relativeLabel}
          />
          {hasDateError && isEndDate && (
            <div style={{ position: 'absolute', bottom: '-18px', left: 0, fontSize: '11px', color: '#dc3545', whiteSpace: 'nowrap' }}>
              {hasDateError}
            </div>
          )}
        </div>
      );
    }

    return (
      <textarea
        value={value || ''}
        onChange={(e) => {
          const el = e.target;
          el.style.height = 'auto';
          el.style.height = `${el.scrollHeight}px`;
          setSchedules(prev => prev.map(s => s.id === schedule.id ? { ...s, [field]: e.target.value } : s));
        }}
        onBlur={() => saveCell(schedule.id, field, value)}
        style={autoResizeTaStyle}
        rows={1}
        disabled={!canEdit}
        data-auto-resize="true"
      />
    );
  };

  const renderTzConstructionCell = (displayRow, field, value) => {
    const isSynced = displayRow.isSyncedFromConstruction;
    const editable = canEdit && isTzFieldEditable(field, isSynced);
    const isDateField = field.includes('date');
    const hasDateError = dateErrors[`${displayRow.id}_${field.includes('actual') ? 'fact' : 'plan'}`];

    const updateRow = (patch) => {
      const allowed = Object.fromEntries(
        Object.entries(patch).filter(([k]) => isTzFieldEditable(k, isSynced))
      );
      if (!Object.keys(allowed).length) return;
      setTzConstructionSchedules((prev) =>
        prev.map((s) => (s.id === displayRow.id ? { ...s, ...allowed } : s))
      );
    };

    const readonlyBox = (text, title) => (
      <div
        title={title || 'Даты из графика строительства (только просмотр)'}
        style={{
          padding: '6px 8px',
          backgroundColor: 'rgba(13, 110, 253, 0.12)',
          borderRadius: '4px',
          color: 'var(--text-primary)',
          fontSize: '13px',
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text || '—'}
      </div>
    );

    if (field === 'construction_stage') {
      if (!editable) return readonlyBox(value, '№ п/п');
      return (
        <input
          type="text"
          value={value || ''}
          placeholder="№"
          onChange={(e) => updateRow({ [field]: e.target.value })}
          onBlur={() => saveTzConstructionCell(displayRow.id, field, value, isSynced)}
          style={{ width: '100%', textAlign: 'center', fontWeight: 500 }}
          disabled={!editable}
        />
      );
    }

    if (field === 'sections') {
      if (!editable) {
        return (
          <StageColoredReadonly
            text={value}
            stages={stages}
            title="Этап из графика строительства (только просмотр)"
          />
        );
      }
      return (
        <StageAutocomplete
          value={value || ''}
          stages={stages}
          disabled={!editable}
          onChange={(newValue) => {
            updateRow({ sections: newValue });
            saveTzConstructionCell(displayRow.id, 'sections', newValue, isSynced);
          }}
          onBlur={() => saveTzConstructionCell(displayRow.id, 'sections', value, isSynced)}
        />
      );
    }

    if (field === 'work_name') {
      if (!editable) return readonlyBox(value, 'Совпадает со стройкой');
      return (
        <textarea
          value={value || ''}
          placeholder="Наименование работ"
          onChange={(e) => {
            const el = e.target;
            el.style.height = 'auto';
            el.style.height = `${el.scrollHeight}px`;
            updateRow({ [field]: e.target.value });
          }}
          onBlur={() => saveTzConstructionCell(displayRow.id, field, value, isSynced)}
          style={autoResizeTaStyle}
          rows={1}
          disabled={!editable}
          data-auto-resize="true"
        />
      );
    }

    if (isDateField) {
      if (!editable) {
        return readonlyBox(value ? formatDateDisplay(value) : '', 'Дата из /construction-schedule');
      }
      const isEndDate = field.includes('end');
      const relatedDate = field === 'planned_end_date' ? displayRow.planned_start_date
        : field === 'actual_start_date' ? displayRow.planned_start_date
        : field === 'actual_end_date' ? displayRow.planned_end_date
        : null;
      const relativeLabel = field === 'actual_end_date' ? 'От даты окончания' : 'От даты начала';
      return (
        <div style={{ position: 'relative' }}>
          <QuickDatePicker
            value={value}
            onChange={(newValue) => updateRow({ [field]: newValue })}
            onSave={(newValue) => saveTzConstructionCell(displayRow.id, field, newValue, isSynced)}
            disabled={!editable}
            isEndDate={isEndDate}
            relatedDate={relatedDate}
            relativeLabel={relativeLabel}
          />
          {hasDateError && isEndDate && (
            <div style={{ position: 'absolute', bottom: '-18px', left: 0, fontSize: '11px', color: '#dc3545', whiteSpace: 'nowrap' }}>
              {hasDateError}
            </div>
          )}
        </div>
      );
    }

    return (
      <textarea
        value={value || ''}
        onChange={(e) => {
          const el = e.target;
          el.style.height = 'auto';
          el.style.height = `${el.scrollHeight}px`;
          updateRow({ [field]: e.target.value });
        }}
        onBlur={() => saveTzConstructionCell(displayRow.id, field, value, isSynced)}
        style={autoResizeTaStyle}
        rows={1}
        disabled={!editable}
        data-auto-resize="true"
      />
    );
  };

  const ReadinessBar = ({ value }) => {
    if (value === null || value === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    const color = value >= 100 ? '#28a745' : value >= 50 ? '#ffc107' : '#007bff';
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <div style={{ flex: 1, height: '8px', backgroundColor: 'var(--table-stripe)', borderRadius: '4px', overflow: 'hidden', minWidth: '40px' }}>
          <div style={{ width: `${value}%`, height: '100%', backgroundColor: color, borderRadius: '4px', transition: 'width 0.3s' }} />
        </div>
        <span style={{ fontSize: '12px', fontWeight: 500, minWidth: '35px', textAlign: 'right' }}>{value}%</span>
      </div>
    );
  };

  const DelayBadge = ({ days }) => {
    if (days === null || days === undefined) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    const color = days > 0 ? '#dc3545' : days < 0 ? '#28a745' : '#6c757d';
    const prefix = days > 0 ? '+' : '';
    return (
      <span style={{
        color, fontWeight: 600, fontSize: '13px',
        padding: '2px 8px', borderRadius: '4px',
        backgroundColor: days > 0 ? '#f8d7da' : days < 0 ? '#d4edda' : 'transparent'
      }}>
        {prefix}{days} дн.
      </span>
    );
  };

  const baseFiltered = getFilteredSchedules().filter(
    (s) => !hiddenPreconstructionIds.has(String(s.id))
  );
  const chapterColorMap = buildChapterColorMap(baseFiltered);
  const { groups: outlineGroups, levels: outlineLevels } = buildOutlineGroups(baseFiltered);
  const visibleSchedules = baseFiltered.filter(
    (_, idx) => !isRowHiddenByOutline(idx, outlineGroups, collapsedGroupParentIds, baseFiltered)
  );
  const hiddenByOutlineCount = baseFiltered.length - visibleSchedules.length;
  const hiddenPreconstructionCount = hiddenPreconstructionIds.size;
  const hasOutline = outlineGroups.size > 0;
  const totalCols = (canEdit ? 14 : 13) + (hasOutline ? 1 : 0);

  const stopPanelToggle = (e) => e.stopPropagation();

  const baseTzFiltered = getFilteredTzConstructionDisplay().filter(
    (s) => !hiddenTzConstructionIds.has(String(s.id))
  );
  const { groups: tzOutlineGroups, levels: tzOutlineLevels } = buildOutlineGroups(baseTzFiltered);
  const visibleTzConstructionRows = baseTzFiltered.filter(
    (_, idx) => !isRowHiddenByOutline(idx, tzOutlineGroups, collapsedTzGroupParentIds, baseTzFiltered)
  );
  const hiddenTzByOutlineCount = baseTzFiltered.length - visibleTzConstructionRows.length;
  const hiddenTzConstructionCount = hiddenTzConstructionIds.size;
  const hasTzOutline = tzOutlineGroups.size > 0;
  const constructionColSpan =
    (canEdit ? 13 : 12) + (hasTzOutline ? 1 : 0);
  const syncedCount = tzConstructionDisplayRows.filter((r) => r.isSyncedFromConstruction).length;

  const constructionToolbar = (
    <div onClick={stopPanelToggle} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <input
        type="text"
        placeholder="Поиск по №, этапу или наименованию работ..."
        value={constructionSearchText}
        onChange={(e) => setConstructionSearchText(e.target.value)}
        style={{
          padding: '8px 12px',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          width: '100%',
          maxWidth: '420px',
          fontSize: '14px',
        }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {canEdit && (
          <GlassButton
            onClick={fillFromTzConstructionTemplate}
            icon="📋"
            label={tzConstructionTemplateLoading ? 'Загрузка...' : 'Заполнить по шаблону'}
            variant="purple"
            disabled={tzConstructionTemplateLoading}
          />
        )}
        {canEdit && tzConstructionSchedules.length > 0 && (
          <GlassButton
            onClick={deleteAllTzConstructionForCity}
            icon="🗑️"
            label="Удалить всё"
            variant="danger"
          />
        )}
        <GlassButton
          onClick={fetchConstructionSourceSchedules}
          icon="🔄"
          label="Обновить даты со стройки"
          variant="muted"
        />
        {baseTzFiltered.length > 0 && hasTzOutline && (
          <>
            <GlassButton onClick={expandAllTzGroups} icon="📂" label="Развернуть все" variant="info" />
            <GlassButton onClick={collapseAllTzGroups} icon="📁" label="Свернуть все" variant="muted" />
            {hiddenTzByOutlineCount > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Свернуто строк: {hiddenTzByOutlineCount}
              </span>
            )}
          </>
        )}
        {hiddenTzConstructionCount > 0 && (
          <>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Скрыто вручную: {hiddenTzConstructionCount}
            </span>
            <GlassButton
              onClick={() => unhideAllRows(TZ_CONSTRUCTION_SCHEDULE_TYPE)}
              icon="👁"
              label="Показать скрытые"
              variant="muted"
            />
          </>
        )}
        <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
          {syncedCount} работ со стройки — даты только просмотр (голубая подсветка)
        </span>
      </div>
    </div>
  );

  const preconstructionToolbar = (
    <div onClick={stopPanelToggle} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <input
        type="text"
        placeholder="Поиск по №, этапу или наименованию работ..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{
          padding: '8px 12px',
          border: '1px solid var(--border-color)',
          borderRadius: '6px',
          width: '100%',
          maxWidth: '420px',
          fontSize: '14px',
        }}
      />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'center' }}>
        {schedules.length > 0 && outlineGroups.size > 0 && (
          <>
            <GlassButton
              onClick={expandAllGroups}
              icon="📂"
              label="Развернуть все"
              variant="info"
            />
            <GlassButton
              onClick={collapseAllGroups}
              icon="📁"
              label="Свернуть все"
              variant="muted"
            />
            {hiddenByOutlineCount > 0 && (
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                Свернуто строк: {hiddenByOutlineCount}
              </span>
            )}
          </>
        )}
        {hiddenPreconstructionCount > 0 && (
          <>
            <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              Скрыто вручную: {hiddenPreconstructionCount}
            </span>
            <GlassButton
              onClick={() => unhideAllRows(SCHEDULE_TYPE)}
              icon="👁"
              label="Показать скрытые"
              variant="muted"
            />
          </>
        )}
        {canEdit && (
          <>
            <GlassButton
              onClick={fillFromTemplate}
              icon="📋"
              label={templateLoading ? 'Загрузка...' : 'Заполнить по шаблону'}
              variant="info"
              disabled={templateLoading}
            />
            {schedules.length > 0 && (
              <GlassButton
                onClick={deleteAllForCity}
                icon="🗑️"
                label="Удалить всё"
                variant="danger"
              />
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="container-fluid">
      <h1>График ТЗ до начала строительства</h1>
      
      <CitySearchSelect cities={cities} value={selectedCity} onChange={handleCityChange} />

      <ScheduleToolbar
        schedules={visibleSchedules}
        columns={EXPORT_COLUMNS}
        filename="preconstruction_schedule"
        scheduleType="preconstruction"
        canEdit={canEdit}
        cities={cities}
        selectedCity={selectedCity}
        tzCalendarModes={TZ_CALENDAR_MODES}
        calendarMode={calendarMode}
        onCalendarModeChange={handleCalendarModeChange}
      />

      {calendarMode ? (
        <div className="card-full-width">
          <ModernGanttChart
            schedules={calendarSchedules}
            cities={cities}
            selectedView={calendarMode === 'construction' ? TZ_CONSTRUCTION_SCHEDULE_TYPE : SCHEDULE_TYPE}
            chartTitle={CALENDAR_MODE_TITLES[calendarMode]}
            allowedScheduleTypes={calendarAllowedTypes}
            onScheduleUpdate={handleScheduleUpdate}
            dependencyCityId={selectedCity}
          />
        </div>
      ) : (
        <>
          <ScheduleSectionPanel
            title="График до начала строительства"
            subtitle="Полный перечень этапов и работ до начала строительства"
            badge={visibleSchedules.length > 0 ? `${visibleSchedules.length} строк` : null}
            expanded={mainTableExpanded}
            onToggle={toggleMainTable}
            accent="#007bff"
            toolbar={preconstructionToolbar}
          >
          <div style={{ padding: 0, overflow: 'visible' }}>
            <SyncedHorizontalScroll
              scrollbarPosition="bottom"
              maxHeight="calc(100vh - 280px)"
              contentKey={`${visibleSchedules.length}-${hasOutline ? 1 : 0}-${selectedCity || ''}-${collapsedGroupParentIds.size}`}
              data-testid="preconstruction-table-scroll"
              style={{ padding: 0 }}
            >
              <table className="table po-table po-table-auto-height" style={{ marginBottom: 0, borderCollapse: 'collapse', border: cellBorder }}>
                <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--table-stripe)', zIndex: 10, boxShadow: 'inset 0 -1px 0 var(--border-color)' }}>
                  <tr>
                    {hasOutline && <th style={{ width: '28px', border: cellBorder, padding: '4px', minWidth: '28px' }} />}
                    <th style={{ width: '45px', border: cellBorder, padding: cellPad, textAlign: 'center' }}>№</th>
                    {canEdit && <th style={{ width: '80px', border: cellBorder, padding: cellPad }}>Действия</th>}
                    <th style={{ minWidth: '90px', width: '90px', border: cellBorder, padding: cellPad, textAlign: 'center' }}>№ п/п</th>
                    <th style={{ minWidth: '220px', border: cellBorder, padding: cellPad }}>Этап строительства</th>
                    <th style={{ minWidth: '320px', border: cellBorder, padding: cellPad }}>Наименование работ</th>
                    <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>План начало</th>
                    <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>План конец</th>
                    <th style={{ width: '110px', border: cellBorder, padding: cellPad }}>Готовность, %</th>
                    <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>Факт начало</th>
                    <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>Факт конец</th>
                    <th style={{ width: '110px', border: cellBorder, padding: cellPad }}>Факт готовн., %</th>
                    <th style={{ width: '130px', border: cellBorder, padding: cellPad }}>Отставание/опережение</th>
                    <th style={{ minWidth: '140px', border: cellBorder, padding: cellPad }}>Исполнитель</th>
                    <th style={{ minWidth: '140px', border: cellBorder, padding: cellPad }}>Ответственный</th>
                    <th style={{ minWidth: '160px', border: cellBorder, padding: cellPad }}>Документы</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleSchedules.map((schedule, index) => {
                    const baseIdx = baseFiltered.findIndex(s => s.id === schedule.id);
                    const isGroupParent = hasOutline && outlineGroups.has(baseIdx);
                    const isCollapsed = isGroupParent && collapsedGroupParentIds.has(String(schedule.id));
                    const level = hasOutline ? outlineLevels[baseIdx] : 0;
                    const isSection = isSectionRow(schedule);
                    const cellFontWeight = getHierarchyCellFontWeight(schedule);
                    const planReadiness = calcReadiness(schedule.planned_start_date, schedule.planned_end_date);
                    const factReadiness = calcReadiness(schedule.actual_start_date, schedule.actual_end_date);
                    const delay = calcDelay(schedule.planned_end_date, schedule.actual_end_date);

                    const rowStyle = getHierarchyRowStyle(schedule, {
                      isNew: schedule.isNew,
                      colorizeByChapter: true,
                      chapterColorMap,
                      rowIndex: baseIdx,
                      rows: baseFiltered,
                    });
                    const outlineBorderColor = getOutlineIndentBorderColor(
                      schedule,
                      baseIdx,
                      baseFiltered,
                      chapterColorMap,
                      true
                    );

                    return (
                      <tr key={schedule.id} style={rowStyle}>
                        {hasOutline && (
                          <td style={{
                            width: '28px', minWidth: '28px', border: cellBorder, padding: '2px 4px',
                            verticalAlign: 'middle',
                            background: isSection ? undefined : 'var(--table-stripe)',
                          }}
                          >
                            {isGroupParent ? (
                              <button
                                type="button"
                                onClick={() => toggleGroupCollapse(schedule.id)}
                                style={{
                                  width: '20px', height: '20px', padding: 0, border: '1px solid #94a3b8',
                                  borderRadius: '3px', background: '#fff', cursor: 'pointer',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  fontSize: '14px', lineHeight: 1, color: '#64748b'
                                }}
                                title={isCollapsed ? 'Развернуть' : 'Свернуть'}
                              >
                                {isCollapsed ? '+' : '−'}
                              </button>
                            ) : level >= 0 ? (
                              <div style={{
                                width: '100%', minHeight: '20px',
                                borderLeft: `2px solid ${outlineBorderColor}`, marginLeft: '10px',
                              }}
                              />
                            ) : null}
                          </td>
                        )}
                        <td style={{ textAlign: 'center', border: cellBorder, padding: cellPad, fontSize: '12px', color: 'var(--text-muted)' }}>
                          {schedule.isNew ? '★' : index + 1}
                        </td>
                        {canEdit && (
                          <td style={{ textAlign: 'center', border: cellBorder, padding: cellPad }}>
                            <RowActions
                              onCopy={() => copyRow(schedule)}
                              onDelete={() => deleteRow(schedule.id)}
                              onHide={() => hideRow(schedule.id, SCHEDULE_TYPE)}
                              onMoveUp={() => moveRow(index, index - 1)}
                              onMoveDown={() => moveRow(index, index + 1)}
                              onInsertAbove={() => insertRowAt(index, 'above')}
                              onInsertBelow={() => insertRowAt(index, 'below')}
                              canMoveUp={index > 0}
                              canMoveDown={index < visibleSchedules.length - 1}
                            />
                          </td>
                        )}
                        <td style={{ border: cellBorder, padding: cellPad, textAlign: 'center', fontWeight: cellFontWeight, whiteSpace: 'nowrap', minWidth: '90px', width: '90px' }}>
                          {renderCell(schedule, 'construction_stage', schedule.construction_stage)}
                        </td>
                        <td style={{
                          border: cellBorder, padding: cellPad,
                          fontWeight: cellFontWeight,
                          color: schedule.sections ? 'inherit' : 'var(--text-muted)',
                          fontSize: isSection ? 'inherit' : '13px'
                        }}>
                          {renderCell(schedule, 'sections', schedule.sections)}
                        </td>
                        <td style={{
                          border: cellBorder, padding: cellPad, fontWeight: cellFontWeight,
                          paddingLeft: hasOutline && level > 0 ? 8 + level * 16 : undefined
                        }}>
                          {renderCell(schedule, 'work_name', schedule.work_name)}
                        </td>
                        <td style={cellTd}>{renderCell(schedule, 'planned_start_date', schedule.planned_start_date)}</td>
                        <td style={cellTd}>{renderCell(schedule, 'planned_end_date', schedule.planned_end_date)}</td>
                        <td style={cellTd}><ReadinessBar value={planReadiness} /></td>
                        <td style={cellTd}>{renderCell(schedule, 'actual_start_date', schedule.actual_start_date)}</td>
                        <td style={cellTd}>{renderCell(schedule, 'actual_end_date', schedule.actual_end_date)}</td>
                        <td style={cellTd}><ReadinessBar value={factReadiness} /></td>
                        <td style={{ border: cellBorder, padding: cellPad, textAlign: 'center' }}><DelayBadge days={delay} /></td>
                        <td style={cellTd}>{renderCell(schedule, 'service', schedule.service)}</td>
                        <td style={cellTd}>{renderCell(schedule, 'responsible_employee', schedule.responsible_employee)}</td>
                        <td style={cellTd}>{renderCell(schedule, 'vacancy', schedule.vacancy)}</td>
                      </tr>
                    );
                  })}
                  {visibleSchedules.length === 0 && (
                    <tr>
                      <td colSpan={totalCols} style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
                        {hiddenByOutlineCount > 0 ? (
                          <>
                            <div style={{ fontSize: '36px', marginBottom: '12px' }}>👁‍🗨</div>
                            <div>Скрыто строк: {hiddenByOutlineCount}</div>
                            <button onClick={expandAllGroups} className="btn btn-primary" style={{ marginTop: '16px' }}>
                              Развернуть все
                            </button>
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                            <div>Нет данных для отображения</div>
                            {canEdit && (
                              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginTop: '16px' }}>
                                <button onClick={fillFromTemplate} className="btn btn-primary" disabled={templateLoading}>
                                  {templateLoading ? 'Загрузка...' : 'Заполнить по шаблону'}
                                </button>
                                <button onClick={addNewRow} className="btn btn-secondary">
                                  + Добавить строку
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                  {visibleSchedules.length > 0 && canEdit && (
                    <AddRowButtonCompact onClick={addNewRow} colSpan={totalCols} />
                  )}
                </tbody>
              </table>
            </SyncedHorizontalScroll>
          </div>


          </ScheduleSectionPanel>

          <ScheduleSectionPanel
            title="График строительства"
            subtitle="Работы ТЗ и даты со стройки (только просмотр)"
            badge={tzConstructionSchedules.length > 0 ? `${tzConstructionSchedules.length} строк` : 'Пусто'}
            expanded={secondaryTableExpanded}
            onToggle={toggleSecondaryTable}
            accent="#6f42c1"
            toolbar={constructionToolbar}
          >
            <div style={{ padding: 0, overflow: 'visible' }}>
              <SyncedHorizontalScroll
                scrollbarPosition="bottom"
                maxHeight="calc(100vh - 280px)"
                contentKey={`${visibleTzConstructionRows.length}-${hasTzOutline ? 1 : 0}-${selectedCity || ''}-${collapsedTzGroupParentIds.size}`}
                data-testid="tz-construction-table-scroll"
                style={{ padding: 0 }}
              >
                <table className="table po-table po-table-auto-height" style={{ marginBottom: 0, borderCollapse: 'collapse', border: cellBorder }}>
                  <thead style={{ position: 'sticky', top: 0, backgroundColor: 'var(--table-stripe)', zIndex: 10, boxShadow: 'inset 0 -1px 0 var(--border-color)' }}>
                    <tr>
                      {hasTzOutline && <th style={{ width: '28px', border: cellBorder, padding: '4px', minWidth: '28px' }} />}
                      <th style={{ width: '45px', border: cellBorder, padding: cellPad, textAlign: 'center' }}>№</th>
                      {canEdit && <th style={{ width: '80px', border: cellBorder, padding: cellPad }}>Действия</th>}
                      <th style={{ minWidth: '90px', border: cellBorder, padding: cellPad, textAlign: 'center' }}>№ п/п</th>
                      <th style={{ minWidth: '220px', border: cellBorder, padding: cellPad }}>Этап строительства</th>
                      <th style={{ minWidth: '320px', border: cellBorder, padding: cellPad }}>Наименование работ</th>
                      <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>План начало</th>
                      <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>План конец</th>
                      <th style={{ width: '110px', border: cellBorder, padding: cellPad }}>Готовность, %</th>
                      <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>Факт начало</th>
                      <th style={{ minWidth: '120px', border: cellBorder, padding: cellPad }}>Факт конец</th>
                      <th style={{ width: '130px', border: cellBorder, padding: cellPad }}>Отставание/опережение</th>
                      <th style={{ minWidth: '140px', border: cellBorder, padding: cellPad }}>Исполнитель</th>
                      <th style={{ minWidth: '140px', border: cellBorder, padding: cellPad }}>Ответственный</th>
                      <th style={{ minWidth: '160px', border: cellBorder, padding: cellPad }}>Документы</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleTzConstructionRows.length === 0 ? (
                      <tr>
                        <td colSpan={constructionColSpan} style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--text-muted)', border: cellBorder }}>
                          <div style={{ fontSize: '40px', marginBottom: '12px', opacity: 0.5 }}>📊</div>
                          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '6px' }}>Таблица пока пуста</div>
                          <div style={{ fontSize: '13px' }}>
                            Нажмите «Заполнить по шаблону»
                          </div>
                        </td>
                      </tr>
                    ) : (
                      visibleTzConstructionRows.map((row, index) => {
                        const baseIdx = baseTzFiltered.findIndex((s) => s.id === row.id);
                        const isGroupParent = hasTzOutline && tzOutlineGroups.has(baseIdx);
                        const isCollapsed = isGroupParent && collapsedTzGroupParentIds.has(String(row.id));
                        const level = hasTzOutline ? tzOutlineLevels[baseIdx] : 0;
                        const isSynced = row.isSyncedFromConstruction;
                        const cellFontWeight = getHierarchyCellFontWeight(row);
                        const planReadiness = calcReadiness(row.planned_start_date, row.planned_end_date);
                        const delay = calcDelay(row.planned_end_date, row.actual_end_date);
                        const stageLabel = row.sections || '';
                        const rowStyle = getHierarchyRowStyle(row, {
                          isNew: row.isNew,
                          isSynced,
                          isTzTable: true,
                        });
                        return (
                          <tr key={row.id} style={rowStyle}>
                            {hasTzOutline && (
                              <td style={{
                                width: '28px', minWidth: '28px', border: cellBorder, padding: '2px 4px',
                                verticalAlign: 'middle', background: 'var(--table-stripe)',
                              }}
                              >
                                {isGroupParent ? (
                                  <button
                                    type="button"
                                    onClick={() => toggleTzGroupCollapse(row.id)}
                                    style={{
                                      width: '20px', height: '20px', padding: 0, border: '1px solid #94a3b8',
                                      borderRadius: '3px', background: '#fff', cursor: 'pointer',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      fontSize: '14px', lineHeight: 1, color: '#64748b',
                                    }}
                                    title={isCollapsed ? 'Развернуть' : 'Свернуть'}
                                  >
                                    {isCollapsed ? '+' : '−'}
                                  </button>
                                ) : level >= 0 ? (
                                  <div style={{
                                    width: '100%', minHeight: '20px',
                                    borderLeft: '2px solid #cbd5e1', marginLeft: '10px',
                                  }}
                                  />
                                ) : null}
                              </td>
                            )}
                            <td style={{ textAlign: 'center', border: cellBorder, padding: cellPad, fontSize: '12px', color: 'var(--text-muted)' }}>
                              {row.isNew ? '★' : index + 1}
                            </td>
                            {canEdit && (
                              <td style={{ textAlign: 'center', border: cellBorder, padding: cellPad }}>
                                <RowActions
                                  onCopy={() => {
                                    if (!guardSyncedRow(row)) copyTzConstructionRow(row);
                                  }}
                                  onDelete={() => {
                                    if (!guardSyncedRow(row)) deleteTzConstructionRow(row.id);
                                  }}
                                  onHide={() => {
                                    if (!guardSyncedRow(row)) hideRow(row.id, TZ_CONSTRUCTION_SCHEDULE_TYPE);
                                  }}
                                  onMoveUp={() => moveTzConstructionRow(index, index - 1, visibleTzConstructionRows)}
                                  onMoveDown={() => moveTzConstructionRow(index, index + 1, visibleTzConstructionRows)}
                                  onInsertAbove={() => insertTzConstructionRowAt(index, 'above', visibleTzConstructionRows)}
                                  onInsertBelow={() => insertTzConstructionRowAt(index, 'below', visibleTzConstructionRows)}
                                  canMoveUp={index > 0}
                                  canMoveDown={index < visibleTzConstructionRows.length - 1}
                                  disableDelete={isSynced}
                                  disableHide={isSynced}
                                  disableMove={isSynced}
                                  disableInsert={isSynced}
                                  disableCopy={isSynced}
                                  disabledTitle={SYNCED_ROW_GUARD_MSG}
                                />
                              </td>
                            )}
                            <td style={{ border: cellBorder, padding: cellPad, textAlign: 'center', fontWeight: cellFontWeight }}>
                              {renderTzConstructionCell(row, 'construction_stage', row.construction_stage || String(index + 1))}
                            </td>
                            <td style={{ ...cellTd, fontWeight: cellFontWeight }}>
                              {renderTzConstructionCell(row, 'sections', stageLabel || row.sections)}
                            </td>
                            <td style={{
                              ...cellTd,
                              fontWeight: cellFontWeight,
                              paddingLeft: hasTzOutline && level > 0 ? 8 + level * 16 : undefined,
                            }}
                            >
                              {renderTzConstructionCell(row, 'work_name', row.work_name)}
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'planned_start_date', row.planned_start_date)}
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'planned_end_date', row.planned_end_date)}
                            </td>
                            <td style={cellTd}>
                              <ReadinessBar value={planReadiness} />
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'actual_start_date', row.actual_start_date)}
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'actual_end_date', row.actual_end_date)}
                            </td>
                            <td style={cellTd}>
                              <DelayBadge days={delay} />
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'service', row.service)}
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'responsible_employee', row.responsible_employee)}
                            </td>
                            <td style={cellTd}>
                              {renderTzConstructionCell(row, 'vacancy', row.vacancy)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </SyncedHorizontalScroll>
            </div>
          </ScheduleSectionPanel>

          {!canEdit && (
            <div style={{ marginTop: '10px', padding: '10px', backgroundColor: 'var(--table-stripe)', borderRadius: '4px', color: 'var(--text-muted)', fontSize: '14px' }}>
              <i>Режим просмотра. У вас нет прав для редактирования этого графика.</i>
            </div>
          )}
        </>
      )}
      {deleteAllModal}
    </div>
  );
};

export default PreConstructionSchedule;
